import React from 'react';
import BrandWordmark from '../components/BrandWordmark';
import AppUpdateBanner from '../components/AppUpdateBanner';
import {
  Box,
  useTheme,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { 
  Dashboard as DashboardIcon,
  Logout as LogoutIcon,
  CompareArrows as TransferIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { useNavigate, Outlet } from 'react-router-dom';

const MultiBusinessLayout: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const handleLogout = () => {
    // Clear all auth-related items from localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('multiBusinessManagerToken');
    localStorage.removeItem('inBusinessContext');
    localStorage.removeItem('businessName');
    localStorage.removeItem('businessId');
    
    // Use window.location.href for a full page reload to ensure clean state
    window.location.href = '/login';
  };

  const navigateTo = (path: string) => {
    navigate(path);
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <AppUpdateBanner />
      {/* Sidebar */}
      <Box
        sx={{
          width: 240,
          flexShrink: 0,
          height: '100vh',
          bgcolor: 'background.paper',
          borderRight: 1,
          borderColor: 'divider',
          boxShadow: 1,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Logo area */}
        <Box sx={{ 
          p: 2, 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          borderBottom: 1, 
          borderColor: 'divider',
          height: 100,
        }}>
          <img 
            src={process.env.PUBLIC_URL + '/safety.png'} 
            alt="Safety Check Now Logo" 
            style={{ width: 48, height: 48, marginBottom: 8 }} 
          />
          <BrandWordmark variant="h6" compact sx={{ fontSize: '1.1rem', textAlign: 'center', width: '100%' }} />
        </Box>

        {/* Navigation items */}
        <List component="nav" sx={{ flexGrow: 1 }}>
          <ListItem 
            button 
            onClick={() => navigateTo('/multi-business')} 
            selected={window.location.pathname === '/multi-business' && !window.location.search.includes('section=transfer')}
          >
            <ListItemIcon>
              <DashboardIcon color="primary" />
            </ListItemIcon>
            <ListItemText primary="Dashboard" />
          </ListItem>
          <ListItem 
            button 
            onClick={() => navigateTo('/multi-business?section=transfer')} 
            selected={window.location.pathname === '/multi-business' && window.location.search.includes('section=transfer')}
          >
            <ListItemIcon>
              <TransferIcon color="primary" />
            </ListItemIcon>
            <ListItemText primary="Transfer Assets" />
          </ListItem>
          <ListItem 
            button 
            onClick={() => navigateTo('/multi-business/transfer-history')} 
            selected={window.location.pathname === '/multi-business/transfer-history'}
          >
            <ListItemIcon>
              <HistoryIcon color="primary" />
            </ListItemIcon>
            <ListItemText primary="Transfer History" />
          </ListItem>
        </List>

        {/* Logout button at bottom */}
        <List>
          <ListItem
            button
            onClick={handleLogout}
            sx={{
              '&:hover': {
                backgroundColor: theme.palette.error.light,
                color: theme.palette.error.contrastText,
                '& .MuiListItemIcon-root': {
                  color: theme.palette.error.contrastText,
                },
              },
            }}
          >
            <ListItemIcon>
              <LogoutIcon color="error" />
            </ListItemIcon>
            <ListItemText primary="Logout" />
          </ListItem>
        </List>
      </Box>

      {/* Main content area */}
      <Box component="main" sx={{ flexGrow: 1 }}>
        {/* Top Bar */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          height: 64,
          px: 2,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          boxShadow: 1
        }}>
          <Box sx={{ display: { sm: 'none' } }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <img 
                src={process.env.PUBLIC_URL + '/safety.png'} 
                alt="Logo" 
                style={{ width: 32, height: 32, marginRight: 8 }} 
              />
              <BrandWordmark variant="h6" compact sx={{ fontSize: '1rem' }} />
            </Box>
          </Box>
        </Box>

        {/* Page content - will be filled by child routes */}
        <Outlet />
      </Box>
    </Box>
  );
};

export default MultiBusinessLayout;

